import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'

import Wizard, { WizardProps } from '@cloudscape-design/components/wizard'

import Step1 from '../../../components/gcpProjectCreation/Steps/Step1'
import Step2 from '../../../components/gcpProjectCreation/Steps/Step2'
import Tags from '../../../components/gcpProjectCreation/Steps/Tags'
import Review from '../../../components/gcpProjectCreation/Steps/Review'

import { usegcpProjectCreationFormStore } from '../../../stores/gcpProjects/newgcpProjectCreationFormStore'
import { gcpProjectRequest } from '../../../types/gcpProject'
import useCreateNewGcpProject from '../../../hooks/mutate/useCreateNewGcpProject'

import { useGenericTagsManagementFormStore } from '../../../stores/tags/genericTagsManagementFormStore'
import useCreateNewTags from '../../../hooks/mutate/useCreateNewTags'
import { TagsRequest, getTagsApprovalObject } from '../../../types/tags'
import { v4 as uuidv4 } from 'uuid'
import {
	DEFAULT_SANDBOX_CRON_SYNTAX,
	SANDBOX_CRON_TIMEZONE,
	resolveSandboxScheduleCronSyntax
} from '../../../helper/gcpProjectSandboxSchedule'

const getSelectedProjectType = (gcpProjectType: unknown): string => {
	if (typeof gcpProjectType === 'string') {
		return gcpProjectType
	}

	if (
		gcpProjectType &&
		typeof gcpProjectType === 'object' &&
		'value' in gcpProjectType
	) {
		return String((gcpProjectType as { value?: string }).value ?? '')
	}

	return ''
}

const i18nStrings: WizardProps.I18nStrings = {
	stepNumberLabel: (stepNumber: number) => `Step ${stepNumber}`,
	collapsedStepsLabel: (stepNumber: number, stepsCount: number) =>
		`Step ${stepNumber} of ${stepsCount}`,
	skipToButtonLabel: (step: { title: string }, _stepNumber: number) =>
		`Skip to ${step.title}`,
	navigationAriaLabel: 'Steps',
	cancelButton: 'Cancel',
	previousButton: 'Previous',
	nextButton: 'Next',
	submitButton: 'Submit',
	optional: 'optional'
}

const steps: Array<WizardProps.Step> = [
	{
		title: 'Project Types',
		content: <Step1 />
	},
	{
		title: 'Project Details',
		content: <Step2 />
	},
	{
		title: 'Tags Details',
		content: <Tags />
	},
	{
		title: 'Review',
		content: <Review />
	}
]

const convertFormValuesTogcpProjectRequestDetails = (
	formValues: Record<any, any>,
	tagFormValues: Record<any, any>
): Record<string, any> => {
	const gcpProjectType = getSelectedProjectType(formValues.gcpProjectType)
	const isSandboxProject = gcpProjectType === 'Sandbox'
	const infrastructureType = formValues.infrastructureType || 'GKE'
	const isGke = formValues.isGKE ?? infrastructureType === 'GKE'

	return {
		[gcpProjectType]: {
			projectName: tagFormValues.projectName || '',
			gcpProjectName: tagFormValues.projectName || '',
			projectViewerDistributionList:
				formValues[`${gcpProjectType}ProjectViewerDistributionList`],
			budgetAlertsDistributionList:
				formValues[`${gcpProjectType}BudgetAlertsDistributionList`],
			platformOwnerEmail: formValues[`${gcpProjectType}PlatformOwnerEmail`],
			monthlyBudgetCap: formValues[`${gcpProjectType}MonthlyBudgetCap`],
			...(!isSandboxProject
				? {
						infrastructureType,
						isGKE: isGke,
						subnetRequired: isGke ? 'yes' : 'no'
					}
				: {}),
			...(isSandboxProject
				? {
						cronSyntax: resolveSandboxScheduleCronSyntax({
							mode: formValues[`${gcpProjectType}ScheduleMode`],
							frequency: formValues[`${gcpProjectType}CronFrequency`],
							day: formValues[`${gcpProjectType}CronDayOfWeek`],
							dayOfMonth: formValues[`${gcpProjectType}CronDayOfMonth`],
							month: formValues[`${gcpProjectType}CronMonth`],
							time: formValues[`${gcpProjectType}CronTime`],
							cronSyntax:
								formValues[`${gcpProjectType}CronSyntax`] ??
								DEFAULT_SANDBOX_CRON_SYNTAX
						}),
						cronTimezone:
							formValues[`${gcpProjectType}CronTimezone`] ||
							SANDBOX_CRON_TIMEZONE
					}
				: {})
		}
	}
}

const GcpProjectCreationForm: FC = () => {
	const { authState } = useOktaAuth()
	const thisUserEmail = authState?.idToken?.claims.email as string

	const navigate = useNavigate()

	const gcpProjectCreationFormStore = usegcpProjectCreationFormStore()
	const tagCreationFormStore = useGenericTagsManagementFormStore()

	const { mutate: createNewgcpProject, isLoading: isLoadinggcpProject } =
		useCreateNewGcpProject()
	const { mutate: createTags, isLoading: isLoadingTags } = useCreateNewTags()

	const handleSubmit = () => {
		const gcpProjectType = getSelectedProjectType(
			gcpProjectCreationFormStore.formValues.gcpProjectType
		)

		if (!gcpProjectType) {
			return
		}

		const details = convertFormValuesTogcpProjectRequestDetails(
			gcpProjectCreationFormStore.formValues,
			tagCreationFormStore.formValues
		)

		let tagsID = ''
		Object.keys(details).forEach(() => {
			tagsID = tagsID === '' ? uuidv4() : tagsID + ',' + uuidv4()
		})

		const gcpProjectRequest: gcpProjectRequest = {
			requestorID: thisUserEmail,
			action: 'CREATE',
			type: gcpProjectType,
			status: 'PENDING_APPROVAL',
			tagsID,
			details
		}

		const tagRequest: TagsRequest = {
			requestorID: thisUserEmail,
			action: 'CREATE',
			status: 'PENDING_APPROVAL',
			details: tagCreationFormStore.formValues,
			tagsID,
			approvalStatus: getTagsApprovalObject()
		}

		createTags(tagRequest, {
			onSuccess: () => {
				createNewgcpProject(gcpProjectRequest, {
					onSuccess: () => navigate('/gcpProjects')
				})
			}
		})
	}

	const handleCancel = () => {
		gcpProjectCreationFormStore.actions.clearFormValues()
		gcpProjectCreationFormStore.actions.setCurrentStep(0)
		tagCreationFormStore.actions.clearFormValues()
		navigate(-1)
	}

	const handleWizardNavigate = ({
		detail
	}: {
		detail: WizardProps.NavigateDetail
	}) => {
		if (detail.reason === 'next') {
			if (gcpProjectCreationFormStore.currentStep === 2) {
				if (!tagCreationFormStore.isFormValid) return
			} else if (!gcpProjectCreationFormStore.isFormValid) {
				return
			}
		}

		gcpProjectCreationFormStore.actions.setCurrentStep(detail.requestedStepIndex)
	}

	return (
		<Wizard
			i18nStrings={i18nStrings}
			steps={steps}
			activeStepIndex={gcpProjectCreationFormStore.currentStep}
			onNavigate={handleWizardNavigate}
			onSubmit={handleSubmit}
			isLoadingNextStep={isLoadinggcpProject || isLoadingTags}
			onCancel={handleCancel}
		/>
	)
}

export default GcpProjectCreationForm
